import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useGovernmentOfOntarioFlag } from 'utils/featureFlags';
import {
  ONTARIO_GTA_EVENT_STARTED_AT,
  ONTARIO_GTA_EVENT_SUMMARY,
  ONTARIO_GTA_FACILITIES,
  ONTARIO_GTA_LINKED_EVENT_NAME,
  ONTARIO_GTA_ORG_EVENT_DEMO_KEY,
  ONTARIO_GTA_RESOURCES_COMMITTED,
  ONTARIO_GTA_THRESHOLD_DESCRIPTION,
  type OntarioGtaFacility,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/ontarioSnowstormNotification';
import { orgEventKeys, type OrgEvent } from 'hooks/orgEvents/orgEventHooks';

const ONTARIO_GTA_EVENT_REGION = 'Greater Toronto Area';
const ONTARIO_GTA_EVENT_TYPE = 'Winter Storm';

const ensureLocks = new Map<string, Promise<unknown>>();

function ontarioGtaCentroid(sites: OntarioGtaFacility[]): {
  lon: number;
  lat: number;
} {
  if (sites.length === 0) {
    return { lon: -79.3832, lat: 43.6532 };
  }
  const lon = sites.reduce((sum, f) => sum + f.lon, 0) / sites.length;
  const lat = sites.reduce((sum, f) => sum + f.lat, 0) / sites.length;
  return { lon, lat };
}

async function patchOntarioGtaOrgEventIfStale(
  existing: OrgEvent,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<OrgEvent> {
  const needsPatch =
    existing.name !== ONTARIO_GTA_LINKED_EVENT_NAME ||
    existing.summary !== ONTARIO_GTA_EVENT_SUMMARY ||
    existing.resources_committed !== ONTARIO_GTA_RESOURCES_COMMITTED ||
    existing.region !== ONTARIO_GTA_EVENT_REGION ||
    existing.type !== ONTARIO_GTA_EVENT_TYPE ||
    existing.threshold_description !== ONTARIO_GTA_THRESHOLD_DESCRIPTION;
  if (!needsPatch) {
    return existing;
  }

  const patched = await axios.patch<OrgEvent>(
    `${config.apiGateway.org_events}/${existing.id}`,
    {
      name: ONTARIO_GTA_LINKED_EVENT_NAME,
      summary: ONTARIO_GTA_EVENT_SUMMARY,
      resources_committed: ONTARIO_GTA_RESOURCES_COMMITTED,
      region: ONTARIO_GTA_EVENT_REGION,
      type: ONTARIO_GTA_EVENT_TYPE,
      threshold_description: ONTARIO_GTA_THRESHOLD_DESCRIPTION,
    }
  );
  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(existing.group_guid),
  });
  return patched.data;
}

async function ensureOntarioGtaOrgEventForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<OrgEvent> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid },
  });
  const existing = (resp.data ?? []).find(
    (event) => event.demo_key === ONTARIO_GTA_ORG_EVENT_DEMO_KEY
  );
  if (existing) {
    return patchOntarioGtaOrgEventIfStale(existing, queryClient);
  }

  const { lon, lat } = ontarioGtaCentroid(ONTARIO_GTA_FACILITIES);
  const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
    group_guid: groupGuid,
    name: ONTARIO_GTA_LINKED_EVENT_NAME,
    type: ONTARIO_GTA_EVENT_TYPE,
    status: 'Active',
    severity: 'High',
    region: ONTARIO_GTA_EVENT_REGION,
    business_unit: 'Government of Ontario',
    summary: ONTARIO_GTA_EVENT_SUMMARY,
    resources_committed: ONTARIO_GTA_RESOURCES_COMMITTED,
    lon,
    lat,
    started_at: ONTARIO_GTA_EVENT_STARTED_AT,
    creation_kind: 'threshold',
    created_by_user_guid: userGuid,
    threshold_description: ONTARIO_GTA_THRESHOLD_DESCRIPTION,
    demo_key: ONTARIO_GTA_ORG_EVENT_DEMO_KEY,
  });

  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(groupGuid),
  });

  return created.data;
}

export function useEnsureOntarioGtaOrgEvent() {
  const ontarioEnabled = useGovernmentOfOntarioFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ontarioEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `ontario-org-event:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureOntarioGtaOrgEventForGroup({
        groupGuid,
        userGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure Ontario GTA org event', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [ontarioEnabled, groupGuid, userGuid, queryClient]);
}
